/**
 * orb-element.ts — the visible Pulse, mounted into the DOM.
 *
 * Phase 2 deliverable (per Zeq Orb v1 §4 + HulyaPulse §5.D).
 *
 * Responsibilities:
 *   - Inject the orb element into <body> as a locked, pinned, top-of-stack
 *     widget (NOT inside any zone).
 *   - Inject orb.css into <head> so the visual pulse phase-locks to 1.287 Hz.
 *   - Expose an API for state transitions: setState("idle" | "listening" | …).
 *   - Drag-to-reposition (Phase 2 affordance — pure UX, no contracts yet).
 *   - Subscribe to ZeqondClock so the orb knows the current zeqond/phase
 *     (used for tooltip / debug overlay; the visual pulse is CSS-driven).
 *
 * Out of scope for Phase 2:
 *   - Click/voice → agent (Phase 4)
 *   - Contract-bound state transitions (Phase 3)
 *   - Theme variants beyond default (Phase 5)
 */

import { ZeqondClock, type ZeqondTick } from "./zeqond-clock.js";

export type OrbState =
  | "idle"
  | "listening"
  | "thinking"
  | "executing"
  | "settling"
  | "confirmed"
  | "rejected"
  | "offline";

const ORB_ID = "zeq-pulse-orb";
const STYLE_TAG_ID = "zeq-pulse-orb-style";
const STORAGE_KEY_POS = "zeq.pulse.orb-position";

let mounted = false;
let orbEl: HTMLElement | null = null;
let unsubscribeClock: (() => void) | null = null;

/**
 * Mount the orb into the current document. Idempotent — calling again
 * after a successful mount is a no-op.
 *
 * @param css  The orb stylesheet contents. We accept it as a string so
 *             the bundle can inline the CSS at build time (no extra
 *             network round-trip), or pass null to skip injection if
 *             the page already linked /pulse/orb.css.
 */
export function mountOrb(css: string | null = null): HTMLElement {
  if (mounted && orbEl) return orbEl;

  // 1. Inject the stylesheet (locked) once per document.
  //    BUG FIX (2026-05-08): when called from auto-boot with css=null we
  //    used to do NOTHING and the orb rendered as an unstyled <div> —
  //    430×23px, position:static, transparent. Now we always ensure the
  //    stylesheet is present: inline if provided, external <link> otherwise.
  if (!document.getElementById(STYLE_TAG_ID)) {
    if (css) {
      const style = document.createElement("style");
      style.id = STYLE_TAG_ID;
      style.setAttribute("data-zeq-locked", "");
      style.textContent = css;
      document.head.appendChild(style);
    } else {
      const link = document.createElement("link");
      link.id = STYLE_TAG_ID;
      link.rel = "stylesheet";
      link.href = "/pulse/orb/orb.css";
      link.setAttribute("data-zeq-locked", "");
      document.head.appendChild(link);
    }
  }

  // 2. Build the orb element. data-zeq-locked so the agent can't remove it.
  const el = document.createElement("div");
  el.id = ORB_ID;
  el.setAttribute("data-zeq-locked", "");
  el.setAttribute("data-state", "idle");
  el.setAttribute("role", "button");
  el.setAttribute("aria-label", "Zeq Pulse — your state machine");
  el.setAttribute("tabindex", "0");
  el.setAttribute("title", "Zeq Pulse · 1.287 Hz");

  // Three translucent blob layers — Siri-style. Each animates at a
  // different period under mix-blend-screen so colours combine into a
  // continuously shifting haze. CSS owns the visual; we just put the
  // DOM nodes in place.
  for (let i = 1; i <= 3; i++) {
    const blob = document.createElement("span");
    blob.className = `orb-blob orb-blob-${i}`;
    blob.setAttribute("aria-hidden", "true");
    el.appendChild(blob);
  }

  const glyph = document.createElement("span");
  glyph.className = "zeq-glyph";
  // Hebrew letter zayin + sheva. Per spec the simple `זְ` form.
  glyph.textContent = "זְ";
  glyph.setAttribute("aria-hidden", "true");
  el.appendChild(glyph);

  // 3. Restore drag-to-reposition position from localStorage if any.
  const pos = readSavedPosition();
  if (pos) applyPosition(el, pos);

  // 4. Wire dragging.
  installDragHandlers(el);

  // 5. Mount.
  document.body.appendChild(el);
  orbEl = el;
  mounted = true;

  // 5a. Click handler — Phase CJ.3 (2026-05-15) unifies the destination.
  //
  // Workbench-first: every orb tap on every page opens the workbench
  // drawer (the "Pulse" tab). The drawer is loaded by pulse-everywhere.js
  // on every framework page now, including /portal/, /state/admin/, the
  // homepage, and every /s/<slug>/. workbench.js resolves the slug from
  // the URL path on /s/ pages and from Zeq.identity.slug elsewhere.
  //
  // Fallback path: if for any reason the workbench isn't loaded (rare —
  // sandbox-only build, network failure on pulse-everywhere.js), open
  // the old inline panel so the orb tap still does *something*. Polled
  // briefly because workbench.js is deferred and may not have attached
  // window.zeqWorkbench yet on the first tap.
  el.addEventListener("click", (ev) => {
    // Drag-end pointerup also fires click — ignore if we just dragged.
    if (el.classList.contains("dragging")) return;
    ev.stopPropagation();
    openWorkbenchOrFallback();
  });

  // 6. Subscribe to ZeqondClock for the live zeqond/phase tooltip.
  unsubscribeClock = ZeqondClock.on((tick) => updateTooltip(el, tick));
  // First-tick paint immediately so the tooltip is correct before the
  // first scheduled tick.
  updateTooltip(el, ZeqondClock.now());

  // 7. Online / offline awareness — auto-flip to offline state when
  // network drops. Caller can override with setState() at any time.
  window.addEventListener("online", () => {
    if (getState() === "offline") setState("idle");
  });
  window.addEventListener("offline", () => {
    setState("offline");
  });
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    setState("offline");
  }

  return el;
}

// ─── Phase CJ.3 — workbench-first orb tap routing ───────────────────────
//
// The workbench is the framework's single destination for the orb tap.
// On every framework page (homepage, /portal/, /state/admin/, /s/<slug>/,
// SDK, etc.) the workbench scripts ride alongside pulse.js via
// pulse-everywhere.js. This helper polls briefly for window.zeqWorkbench
// (deferred script may not have attached yet on the first tap), then
// opens the drawer and switches to the Pulse tab. If after the polling
// window the workbench is still unavailable we fall back to the legacy
// inline panel so the orb tap is never a dead click.
function openWorkbenchOrFallback(): void {
  // ─── Phase CJ.4 — orb tap opens the framework AI entry ──────────
  // The Workbench has been folded into the new Pulse AI (ai.zeq.me). The
  // orb tap now routes to the user's chosen AI entry, recorded at machine
  // spin-up in localStorage "zeq_pick_mode": "cli" -> the built-in Root
  // CLI app, anything else -> Pulse on ai.zeq.me.
  try {
    let mode = "ai";
    try {
      mode = (localStorage.getItem("zeq_pick_mode") || "ai").toLowerCase();
    } catch { /* private mode / storage blocked */ }
    const dest = mode === "cli"
      ? "https://zeq.me/apps/cli/"
      : "https://ai.zeq.me/";
    window.open(dest, "_blank", "noopener,noreferrer");
    return;
  } catch {
    // Last-resort fallback — open the legacy inline panel so the orb tap
    // is never a dead click (private-mode / popup-blocked edge cases).
    void import("../agent/orb-panel.js").then((m) => m.toggle()).catch(() => {});
  }
}

/** Tear down the orb. Used in tests and for clean module reload. */
export function unmountOrb(): void {
  if (unsubscribeClock) { unsubscribeClock(); unsubscribeClock = null; }
  if (orbEl && orbEl.parentNode) orbEl.parentNode.removeChild(orbEl);
  const style = document.getElementById(STYLE_TAG_ID);
  if (style && style.parentNode) style.parentNode.removeChild(style);
  orbEl = null;
  mounted = false;
}

// ─── State transitions ───────────────────────────────────────────────────

const STATE_AUTO_REVERT_MS: Partial<Record<OrbState, number>> = {
  // settling is a momentary white flash — hold for ~200ms then auto-advance.
  settling: 200,
  // confirmed / rejected hold for ~2 zeqonds then return to idle.
  confirmed: 1554,
  rejected: 1554,
};

let revertTimer: ReturnType<typeof setTimeout> | null = null;

export function setState(next: OrbState): void {
  if (!orbEl) return;
  orbEl.setAttribute("data-state", next);

  if (revertTimer) { clearTimeout(revertTimer); revertTimer = null; }
  const ms = STATE_AUTO_REVERT_MS[next];
  if (ms) {
    revertTimer = setTimeout(() => {
      // settling → confirmed; confirmed/rejected → idle.
      const after: OrbState = next === "settling" ? "confirmed" : "idle";
      setState(after);
    }, ms);
  }
}

export function getState(): OrbState {
  if (!orbEl) return "idle";
  return (orbEl.getAttribute("data-state") as OrbState) || "idle";
}

// ─── Tooltip with current zeqond + phase ─────────────────────────────────

function updateTooltip(el: HTMLElement, tick: ZeqondTick): void {
  // Title attribute is the universal native tooltip — no custom popup
  // needed for Phase 2. Future phases can swap for a richer panel.
  el.setAttribute(
    "title",
    `Zeq Pulse · ${getState()} · 1.287 Hz\nZeqond ${tick.zeqond} · phase ${tick.phase.toFixed(4)}`,
  );
}

// ─── Drag-to-reposition ──────────────────────────────────────────────────

interface OrbPosition { left?: number; top?: number; right?: number; bottom?: number; }

function applyPosition(el: HTMLElement, pos: OrbPosition): void {
  // We position via top/left when the user has dragged the orb;
  // otherwise we leave the default bottom-right CSS rule alone.
  if (pos.left !== undefined) { el.style.left = pos.left + "px"; el.style.right = "auto"; }
  if (pos.top !== undefined)  { el.style.top  = pos.top  + "px"; el.style.bottom = "auto"; }
}

function readSavedPosition(): OrbPosition | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_POS);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function savePosition(pos: OrbPosition): void {
  try { localStorage.setItem(STORAGE_KEY_POS, JSON.stringify(pos)); }
  catch { /* quota — give up silently */ }
}

function installDragHandlers(el: HTMLElement): void {
  let dragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let elStartLeft = 0;
  let elStartTop = 0;
  let pressTimer: ReturnType<typeof setTimeout> | null = null;
  let moved = false;

  el.addEventListener("pointerdown", (ev) => {
    // Only left button + primary touch.
    if (ev.button !== undefined && ev.button !== 0) return;
    moved = false;
    // Drag activation requires holding for 300ms — short clicks are
    // reserved for the agent's voice-trigger affordance (Phase 4).
    pressTimer = setTimeout(() => {
      const rect = el.getBoundingClientRect();
      dragging = true;
      dragStartX = ev.clientX;
      dragStartY = ev.clientY;
      elStartLeft = rect.left;
      elStartTop = rect.top;
      el.classList.add("dragging");
      el.setPointerCapture(ev.pointerId);
    }, 300);
  });

  el.addEventListener("pointermove", (ev) => {
    if (!dragging) return;
    moved = true;
    const dx = ev.clientX - dragStartX;
    const dy = ev.clientY - dragStartY;
    const newLeft = Math.max(8, Math.min(window.innerWidth - 64, elStartLeft + dx));
    const newTop  = Math.max(8, Math.min(window.innerHeight - 64, elStartTop + dy));
    applyPosition(el, { left: newLeft, top: newTop });
  });

  const endDrag = (ev: PointerEvent) => {
    if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; }
    if (!dragging) return;
    el.classList.remove("dragging");
    try { el.releasePointerCapture(ev.pointerId); } catch { /* */ }
    dragging = false;
    if (moved) {
      const rect = el.getBoundingClientRect();
      savePosition({ left: rect.left, top: rect.top });
    }
  };

  el.addEventListener("pointerup", endDrag);
  el.addEventListener("pointercancel", endDrag);
}

export const Orb = {
  mount: mountOrb,
  unmount: unmountOrb,
  setState,
  getState,
};

export default Orb;
